import { useEffect, useId, useMemo, useState, type KeyboardEvent } from "react";

import { countries, countryName } from "./countries";

export function CountryCombobox({ onChange, value }: { onChange(code: string): void; value: string }) {
  const id = useId();
  const listId = `${id}-list`;
  const [query, setQuery] = useState(() => countryName(value));
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const matches = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    if (!normalized) return countries.slice(0, 12);
    return countries.filter((country) => country.name.toLocaleLowerCase().includes(normalized) || country.code.toLocaleLowerCase().startsWith(normalized)).slice(0, 12);
  }, [query]);

  useEffect(() => {
    if (value) setQuery(countryName(value));
  }, [value]);

  function select(index: number) {
    const country = matches[index];
    if (!country) return;
    setQuery(country.name);
    onChange(country.code);
    setOpen(false);
    setActiveIndex(-1);
  }

  function keyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Escape") {
      event.preventDefault();
      setQuery(countryName(value));
      setOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setOpen(true);
      const direction = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((current) => (current + direction + matches.length) % matches.length);
      return;
    }
    if (event.key === "Enter" && open && activeIndex >= 0) {
      event.preventDefault();
      select(activeIndex);
    }
  }

  return (
    <div className="field country-field">
      <label htmlFor={id}>Country</label>
      <input
        id={id}
        className="field-control input"
        role="combobox"
        aria-autocomplete="list"
        aria-controls={listId}
        aria-expanded={open}
        aria-activedescendant={open && activeIndex >= 0 ? `${id}-option-${activeIndex}` : undefined}
        autoComplete="off"
        value={query}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onChange={(event) => { setQuery(event.target.value); onChange(""); setOpen(true); setActiveIndex(-1); }}
        onFocus={() => setOpen(true)}
        onKeyDown={keyDown}
      />
      {open && matches.length ? (
        <div id={listId} className="country-options" role="listbox" aria-label="Countries">
          {matches.map((country, index) => (
            <button
              key={country.code}
              id={`${id}-option-${index}`}
              type="button"
              role="option"
              aria-selected={country.code === value}
              data-active={index === activeIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(index)}
            >
              <span>{country.name}</span><small>{country.code}</small>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
